/**
 * liveSessionsListViewModel — pure `(reduxState, studentEmail) → page props` for the live-sessions list.
 *
 * Reads `state.data.liveSessions.live_occurrences` (filled by getAllMyNextLiveSessions /
 * AsParent). Day grouping + section titles live here (not in a formatter / not in the DS).
 *
 * Mémoïsé (`createSelector`) sur les tranches qu'il lit et sur l'e-mail : l'écran reste monté
 * derrière les onglets, et sans cela chaque action du store rejouait tri, groupement et adaptateur.
 */
import moment from "moment"
import { createSelector } from "reselect"

import {
    toUILiveSessions,
    type ApiLiveOccurrence,
    type ApiStudentForSubjects,
} from "../../../adapters/parent/LiveSessions/liveSessionListAdapter"
import { type UILiveSession, type UILiveSessionGroup } from "../../../ui/types"
import { selectDisplayedStudent } from "../../shared/displayedStudent"
import { groupByDay } from "../../shared/groupByDay"

export type LiveSessionsListViewModel = {
    /** À plat et triées — ce que l'étape « Quelle séance ? » d'un geste propose. */
    sessions: UILiveSession[]
    groups: UILiveSessionGroup[]
    /**
     * Rien à montrer et la lecture en vol : la page montre son squelette. Le legacy vidait aussi
     * la liste à chaque rafraîchissement ; ici, une liste déjà là reste affichée (`refreshing`).
     */
    loading: boolean
    /** La lecture repart alors que des séances sont à l'écran : seule la flèche de l'en-tête tourne. */
    refreshing: boolean
    error: unknown | null
    /**
     * `payment_url` du compte connecté — le lien de recouvrement Stripe, lu comme sur l'accueil.
     * Il vit sur `currentUser`, pas sur l'élève résolu : c'est la famille qui règle, pas l'enfant.
     */
    paymentUrl: string | null
}

/**
 * À plat et triées. L'horodatage est calculé UNE fois par séance : le comparateur trie des
 * nombres, là où un `moment()` par comparaison en construisait deux à chaque paire.
 */
function sortedByDate(sessions: UILiveSession[]): UILiveSession[] {
    return sessions
        .map((session) => ({ session, at: moment(session.date).valueOf() }))
        .sort((a, b) => a.at - b.at)
        .map(({ session }) => session)
}

/**
 * Resolve the student used for subjects + attendance-flag scoping.
 * Mirrors legacy LiveSessionsListScreen: currentUser → occurrence students → usersList
 * → `{ email }` stub (keeps parent/asParent flows alive before caches resolve).
 */
function resolveStudent(
    currentUser: ApiStudentForSubjects | null,
    usersList: ApiStudentForSubjects[] | undefined,
    studentEmail: string | null,
    occurrences: ApiLiveOccurrence[]
): ApiStudentForSubjects | null {
    if (!studentEmail) return currentUser

    const normalized = studentEmail.toLowerCase()
    const matchesEmail = (s: ApiStudentForSubjects | null | undefined) =>
        !!s?.email && s.email.toLowerCase() === normalized

    if (matchesEmail(currentUser)) return currentUser

    const fromOccurrences = occurrences.flatMap((occ) => occ?.live_session?.students ?? []).find((s) => matchesEmail(s))
    if (fromOccurrences) return fromOccurrences

    const fromUsersList = usersList?.find((s) => matchesEmail(s))
    if (fromUsersList) return fromUsersList

    // Legacy stub — email only — until occurrence / usersList caches catch up.
    return { email: studentEmail }
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

type ApiSlice = { request?: boolean; error?: unknown }
type ApiCurrentUser = (ApiStudentForSubjects & { payment_url?: string | null }) | null

export const liveSessionsListViewModel = createSelector(
    [
        // La tranche que `useLiveSessionsListData` fait partir : « AsParent » dès qu'un e-mail est passé.
        (state: ReduxState, studentEmail?: string | null) =>
            (studentEmail ? state?.api?.getAllMyNextLiveSessionsAsParent : state?.api?.getAllMyNextLiveSessions) as
                ApiSlice | undefined,
        (state: ReduxState) => state?.data?.liveSessions?.live_occurrences as ApiLiveOccurrence[] | undefined,
        (state: ReduxState) => state?.data?.staticData?.school_subjects,
        (state: ReduxState) => selectDisplayedStudent(state) as ApiCurrentUser,
        (state: ReduxState) => state?.data?.tutors?.usersList as ApiStudentForSubjects[] | undefined,
        (_state: ReduxState, studentEmail?: string | null) => studentEmail ?? null,
    ],
    (req, occurrencesOrNil, subjectsOrNil, currentUser, usersList, studentEmail): LiveSessionsListViewModel => {
        const occurrences = occurrencesOrNil ?? []
        const student = resolveStudent(currentUser, usersList, studentEmail, occurrences)

        const sessions = toUILiveSessions(occurrences, {
            student,
            apiSubjects: subjectsOrNil ?? [],
            paymentBlocked: !!student?.is_payment_blocked,
        })

        // Le squelette la première fois seulement : un rafraîchissement garde la liste à l'écran.
        const requesting = !!req?.request
        const hasRows = occurrences.length > 0
        const loading = requesting && !hasRows
        const error = !requesting && !hasRows ? (req?.error ?? null) : null

        return {
            sessions: sortedByDate(sessions),
            groups: groupByDay(sessions),
            loading,
            refreshing: requesting && hasRows,
            error,
            paymentUrl: currentUser?.payment_url ?? null,
        }
    }
)
